import React from 'react'
import { ConfigProvider } from 'antd'
import { useLocation, useNavigate, useRoutes } from 'react-router-dom'
import { Col, Row } from 'antd'
import ja_JP from 'antd/es/locale/ja_JP'
import 'moment/locale/ja'
import qs from 'query-string'
import some from 'lodash/some'
import lodashGet from 'lodash/get'
import TagManager from 'react-gtm-module'

import routeConfig from 'src/routes'
import Notification from './containers/Notification'
import { useAppDispatch, useAppSelector } from './store'
import LoadingSuspense from './components/application/LoadingSuspense'
import { getInfo } from './useCase/user'
import appActions from './store/ui/app'
import LocalStorage from './utils/LocalStorage'
import { PATH } from 'src/pages/auth'
import { PATH as ERROR_PATH } from 'src/pages/error'
import { PATH as PATH_FLOW } from 'src/pages/homeFlow'
import {
  getQueryStringValue,
  getUserTreatmentIdFromParams,
  handleLinkFromMail,
  isTokenLinkSource,
} from './utils/helper'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'
import { PATH as PATH_ADDITIONAL_SHAMPOO } from 'src/pages/additionalShampoo'
import { PATH as PATH_AUTH } from 'src/pages/auth'
import { PATH as PATH_POLICY } from 'src/pages/policy'
import { PATH as PATH_TERMS } from 'src/pages/terms'
import { PATH as PATH_LEGAL_NOTICE } from 'src/pages/legalNotice'
import { fetcherUserTreatmentFromMail } from './useCase/userTreatment'
import { GLOBAL_GTM_ID } from './constants/env-constant'
import { PATH as PATH_VIDEO } from 'src/pages/video'
import { PATH as PATH_CHAT } from 'src/pages/chat'
import { PATH as PATH_KARTE_PLAN } from 'src/pages/kartePlan'
import { PATH as PATH_SHORT_LINK } from 'src/pages/shortLink'

const KEY_LINK_ACCESS_DIRECT = 'default'

TagManager.initialize({
  gtmId: GLOBAL_GTM_ID,
})

if (LocalStorage.getGtmId()) {
  TagManager.initialize({
    gtmId: LocalStorage.getGtmId(),
  })
}

function App() {
  const routes = useRoutes(routeConfig)
  const loading = useAppSelector((state) => state.ui.app.loading)
  const location = useLocation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const user = useAppSelector((state) => state.ui.app.authInfo)
  const accessToken = LocalStorage.accessToken
  const params = qs.parse(location.search)
  const isTokenDeepLink = isTokenLinkSource(params?.from)
  const userTreatmentIdFromLink = getUserTreatmentIdFromParams(params)
  const id = location.pathname.substring(location.pathname.lastIndexOf('/') + 1)
  const TOKEN_LINK_CLEANUP_ROUTERS = [
    PATH_CHAT.CHAT,
    PATH_KARTE_PLAN.PRESCRIPTION_PROPOSAL.replace(':id', id.toString()),
    PATH_VIDEO.VIDEO_INCOMING.replace(':token', id.toString()),
  ]

  const NOT_CHECK_ERROR_ROUTERS = [
    PATH_MY_PAGE.OTHER_MENU,
    PATH_ADDITIONAL_SHAMPOO.PURCHASE_ADDITIONAL_PATH,
    PATH_ADDITIONAL_SHAMPOO.CONFIRM_DELIVERY_PATH,
    PATH_AUTH.LINK_LINE,
    PATH_AUTH.SIGN_IN,
    PATH_AUTH.REGISTER,
    PATH_TERMS.TERMS,
    PATH_POLICY.POLICY,
    PATH_LEGAL_NOTICE.LEGAL_NOTICE,
    PATH_SHORT_LINK.SHORT_LINK_REDIRECT.replace(':token', id.toString()),
    PATH_VIDEO.VIDEO_INTERVIEW,
    PATH_FLOW.FLOW.replace(':clinic_uuid', id.toString()),
    PATH_MY_PAGE.TREATMENT_PATH,
    PATH.PRECAUTION_MODAL_PLAN,
    PATH.PRECAUTION_MODAL_ONLINE,
    PATH.MEDICAL_SPECIALTY,
    PATH.CHECK_USER_EXISTS_MODAL,
    PATH_AUTH.CONTACT,
    PATH_AUTH.LINE_LINK,
  ]

  const NOT_IMPORT_TOKEN = [
    PATH_AUTH.RESET_PASSWORD_PATH,
    PATH_AUTH.SETTING_PASSWORD_PATH,
  ]

  // [SCOPE] Mail/SMS token import removes ?from/?token but keeps userTreatmentId; these deep-link targets must not fall through to the direct-access error guard after cleanup.
  const isTokenLinkCleanupRoute =
    Boolean(userTreatmentIdFromLink) &&
    some(
      TOKEN_LINK_CLEANUP_ROUTERS,
      (link) => location && link === location.pathname,
    )

  React.useEffect(() => {
    if (
      location.key === KEY_LINK_ACCESS_DIRECT &&
      !isTokenLinkCleanupRoute &&
      !some(
        NOT_CHECK_ERROR_ROUTERS,
        (link) => location && link === location.pathname,
      )
    ) {
      if (!isTokenDeepLink) {
        navigate(ERROR_PATH.STATUS_ERROR)
      } else {
        if (user) {
          // set gg tag id
          const gtmId = lodashGet(user, 'clinics[0].gtm_id', '')
          const currentGmtId = LocalStorage.getGtmId()
          LocalStorage.setGtmId(gtmId)
          if (currentGmtId !== gtmId) {
            TagManager.initialize({
              gtmId: gtmId,
            })
          }

          fetcherUserTreatmentAndRedirect()
        }
      }
    }
  }, [user])

  const fetcherUserTreatmentAndRedirect = async () => {
    if (userTreatmentIdFromLink) {
      const userTreatment = await fetcherUserTreatmentFromMail(
        userTreatmentIdFromLink,
        {
          with: 'menu',
        },
      )

      const redirectRoute = handleLinkFromMail(
        location,
        user?.new_state,
        userTreatment,
      )
      redirectRoute && navigate(redirectRoute)
    }
  }

  React.useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }, 10)
  }, [location])

  // [SCOPE] Token links must refresh the selected treatment before route effects use it — affects /chat, /video/incoming, and prescription proposal links opened from mail/SMS.
  // Pre-existing behavior: token import did not overwrite a stale user_treatment_id in localStorage.
  // New behavior: only mail/SMS token links with a userTreatmentId query param update localStorage, while normal app navigation keeps the existing treatment selection.
  React.useEffect(() => {
    const params = qs.parse(location.search)
    const token = getQueryStringValue(params.token)
    const userTreatmentId = getUserTreatmentIdFromParams(params)

    if (isTokenLinkSource(params.from) && userTreatmentId) {
      LocalStorage.setUserTreatmentId(userTreatmentId)
    }

    if (
      token &&
      !some(NOT_IMPORT_TOKEN, (link) => location && link === location.pathname)
    ) {
      const isMailSmsTokenLink = isTokenLinkSource(params.from)
      const needsTokenImport = token !== accessToken || !user?.id

      const cleanTokenFromUrl = () => {
        const newParams = { ...params }
        delete newParams.token
        delete newParams.from
        const newQueryString = qs.stringify(newParams)
        const newUrl =
          location.pathname + (newQueryString ? `?${newQueryString}` : '')
        navigate(newUrl, { replace: true })
      }

      if (isMailSmsTokenLink && needsTokenImport) {
        dispatch(appActions.setTokenLinkImportPending(true))
        LocalStorage.setToken(token)
        void dispatch(
          getInfo({
            with: 'reservations,card,stateTransitions,cycleOrderPaid,latestOrderCanPay,orderPaymented,userTreatments,userTreatments.reservations',
          }),
        ).finally(() => {
          cleanTokenFromUrl()
          dispatch(appActions.setTokenLinkImportPending(false))
        })
      } else {
        if (needsTokenImport) {
          LocalStorage.setToken(token)
          dispatch(
            getInfo({
              with: 'reservations,card,stateTransitions,cycleOrderPaid,latestOrderCanPay,orderPaymented,userTreatments,userTreatments.reservations',
            }),
          )
        }
        cleanTokenFromUrl()
      }
    }

    if ((!user || (user && !user.id)) && accessToken) {
      dispatch(
        getInfo({
          with: 'reservations,card,stateTransitions,cycleOrderPaid,latestOrderCanPay,orderPaymented,addresses,userTreatments,userTreatments.reservation.doctor',
        }),
      )
    }
  }, [])

  return (
    <ConfigProvider locale={ja_JP} componentSize="middle">
      <Row justify="center">
        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          {routes}
          {loading && <LoadingSuspense />}
          <Notification />
        </Col>
      </Row>
    </ConfigProvider>
  )
}

export default App
